import { ComparerParam } from '@/types/comparer';
import { isArray, isObject, isString } from '@/util/typeGuards';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { memo, type FC } from 'react';
import { isEmptyDisplayValue } from '../comparerUtils/comparerUtils';

type ModifiedPropsProps = { param: ComparerParam };

const parseValue = (value: unknown): unknown => {
  if (!isString(value)) return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const ModifiedProps: FC<ModifiedPropsProps> = ({ param }) => {
  const oldValue = parseValue(param.oldValue);
  const newValue = parseValue(param.newValue);
  const colorA = param.changed && !isEmptyDisplayValue(oldValue) ? 'error.main' : 'text.primary';
  const colorB = param.changed && !isEmptyDisplayValue(newValue) ? 'success.main' : 'text.primary';

  const valueOutput = (parsedValue: unknown, compareValue: unknown, color: string) => {
    if (isArray(parsedValue)) {
      const compareItems = isArray(compareValue) ? compareValue.map((item) => JSON.stringify(item)) : [];
      return parsedValue.map((newValue, index) => {
        const stringifiedValue = JSON.stringify(newValue);
        const isChangedValue = !compareItems.includes(stringifiedValue);
        return (
          <Box key={`modifiedProps-${index}`} color={isChangedValue ? color : 'text.primary'}>
            {stringifiedValue}
          </Box>
        );
      });
    }
    if (isObject(parsedValue)) return JSON.stringify(parsedValue);
    if (isEmptyDisplayValue(parsedValue)) return '-';
    return String(parsedValue);
  };

  return (
    <TableRow>
      <TableCell size='small' sx={{ pl: 13 }}>
        <Stack direction='row' alignItems='center'>
          <Typography variant='caption' color='text.secondary'>
            {param.key}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell size='small' sx={{ color: colorA }}>
        {valueOutput(oldValue, newValue, 'error.main')}
      </TableCell>
      <TableCell size='small' sx={{ color: colorB }}>
        {valueOutput(newValue, oldValue, 'success.main')}
      </TableCell>
    </TableRow>
  );
};

const MemoizedModifiedProps = memo(ModifiedProps);

export default MemoizedModifiedProps;
